import { AuditStatus } from '@fsp/types';
import {
  CheckCircleOutlined,
  Close,
  FactCheck,
  FactCheckOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import { AuditProvider, useAudit } from '../contexts/AuditContext';
import { useAuth } from '../contexts/AuthContext';
import AuditChecklistSidebar from '../pages/app/audit/components/AuditChecklistSidebar';
import { getAuditStatusColor, getAuditStatusLabel } from '../pages/app/audit/utils/auditHelpers';
import { completeAudit, updateAudit } from '../services/audit';

/**
 * Inner component that uses AuditContext
 */
const AuditLayoutContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { organisationId } = useAuth();
  const { planId } = useParams<{ planId: string }>();
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get('audit');

  const { currentAudit, refreshCurrentAudit, plan, release, getSummary } = useAudit();

  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const summary = getSummary();

  const handleSave = async () => {
    if (!organisationId || !planId || !auditId) return;

    setSaving(true);
    try {
      await updateAudit(organisationId, planId, auditId, {
        // Just trigger a save/update timestamp
      });
      enqueueSnackbar('Audit saved successfully', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to save audit', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteClick = () => {
    // Check if audit is incomplete
    if (summary.checkedItems < summary.totalItems) {
      setShowConfirmDialog(true);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (!organisationId || !planId || !auditId) return;

    setShowConfirmDialog(false);
    setCompleting(true);
    try {
      await completeAudit(organisationId, planId, auditId);
      await refreshCurrentAudit();
      enqueueSnackbar('Audit completed successfully', { variant: 'success' });
      navigate(`/app/plans/${planId}/audits/${auditId}/report`);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to complete audit', { variant: 'error' });
    } finally {
      setCompleting(false);
    }
  };

  const handleClose = () => {
    navigate(`/app/plans/${planId}/audits`);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        mr: -10,
      }}
    >
      {/* Audit Header */}
      <AppBar
        position="static"
        variant="outlined"
        elevation={0}
        sx={{
          border: 0,
        }}
      >
        <Toolbar
          sx={{
            bgcolor: 'background.paper',
            mt: -1,
            borderRadius: 1,
            mb: 2,
            mr: 3,
          }}
        >
          <FactCheckOutlined sx={{ mr: 2, color: 'primary.main' }} />
          <Stack direction="column" spacing={0} sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" component="div">
                {currentAudit?.auditType} Audit
              </Typography>
              {release && (
                <Chip label={`v${release.versionLabel}`} size="small" variant="outlined" />
              )}
              {currentAudit && (
                <Chip
                  label={getAuditStatusLabel(currentAudit.status)}
                  size="small"
                  color={getAuditStatusColor(currentAudit.status)}
                />
              )}
            </Stack>
            {plan && (
              <Typography variant="caption" color="text.secondary">
                {plan.name}
              </Typography>
            )}
          </Stack>

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            {currentAudit?.status === AuditStatus.IN_PROGRESS && (
              <>
                <Button
                  variant="outlined"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveOutlined />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  startIcon={completing ? <CircularProgress size={16} /> : <CheckCircleOutlined />}
                  onClick={handleCompleteClick}
                  disabled={completing}
                >
                  Complete Audit
                </Button>
              </>
            )}
            <IconButton edge="end" onClick={handleClose} aria-label="close">
              <Close />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Main Content - 2-panel layout: Center (release content) | Right (audit checklist) */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Center Panel - Release Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>

        {/* Right Panel - Audit Checklist */}
        <Box
          sx={{
            width: 400,
            borderLeft: 1,
            ml: 2,
            mr: 3,
            borderColor: 'divider',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <AuditChecklistSidebar />
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        title="Complete Incomplete Audit?"
        message={`You have only completed ${summary.checkedItems} of ${summary.totalItems} checklist items. Are you sure you want to complete this audit?`}
        confirmLabel="Complete Anyway"
        confirmColor="warning"
        onConfirm={handleComplete}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </Box>
  );
};

/**
 * Layout wrapper that conditionally renders audit UI based on ?audit query param.
 *
 * If ?audit=auditId is present:
 * - Wraps content with AuditProvider
 * - Renders audit header and checklist sidebar
 * - Creates 2-panel layout (content + sidebar)
 *
 * If no ?audit param:
 * - Renders children as-is (normal release view)
 */
export const AuditLayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get('audit');

  // If no audit param, render children normally
  if (!auditId) {
    return <>{children}</>;
  }

  // If audit param present, wrap with audit UI
  return (
    <AuditProvider>
      <AuditLayoutContent>{children}</AuditLayoutContent>
    </AuditProvider>
  );
};

export default AuditLayoutWrapper;
